// src/utils/offlineSnapshot.js
// ══════════════════════════════════════════════════════════════════════════════
// KAILUX — OFFLINE DATA SNAPSHOT  (Phase A2)
// ══════════════════════════════════════════════════════════════════════════════
// Called after every successful API response that returns browseable data.
// Writes a lightweight snapshot to localStorage so OfflinePage.jsx can display
// stale-but-useful information when the network is unavailable.
//
// Usage (in kailux.js interceptors):
//   import { snapshotClients, snapshotAFSHistory, snapshotFirmName } from './offlineSnapshot'
//   snapshotClients(data)
//   snapshotAFSHistory(data)
// ══════════════════════════════════════════════════════════════════════════════

const KEYS = {
  clients:    'kailux_offline_clients',
  afsHistory: 'kailux_offline_afs_history',
  firmName:   'kailux_offline_firm_name',
  timestamp:  'kailux_offline_timestamp',
}

const MAX_CLIENTS     = 100   // cap to avoid filling localStorage
const MAX_AFS_HISTORY = 50

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    localStorage.setItem(KEYS.timestamp, new Date().toISOString())
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

function read(key) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// ── Writers ───────────────────────────────────────────────────────────────────
export function snapshotClients(clients) {
  if (!Array.isArray(clients)) return
  const slim = clients.slice(0, MAX_CLIENTS).map((c) => ({
    id:             c.id,
    name:           c.name,
    entity_type:    c.entity_type,
    year_end_month: c.year_end_month,
    industry:       c.industry,
    status:         c.status,
  }))
  write(KEYS.clients, slim)
}

export function snapshotAFSHistory(jobs) {
  if (!Array.isArray(jobs)) return
  const slim = jobs.slice(0, MAX_AFS_HISTORY).map((j) => ({
    id:              j.id,
    client_id:       j.client_id,
    financial_year:  j.financial_year,
    engagement_type: j.engagement_type,
    status:          j.status,
    created_at:      j.created_at,
    clients:         j.clients ? { name: j.clients.name } : null,
  }))
  write(KEYS.afsHistory, slim)
}

export function snapshotFirmName(name) {
  if (name && typeof name === 'string') {
    write(KEYS.firmName, name)
  }
}

// ── Readers (used by OfflinePage) ─────────────────────────────────────────────
export function readClientsSnapshot()    { return read(KEYS.clients)    || [] }
export function readAFSHistorySnapshot() { return read(KEYS.afsHistory) || [] }
export function readFirmNameSnapshot()   { return read(KEYS.firmName)   || '' }

export function readSnapshotAge() {
  const ts = read(KEYS.timestamp)
  if (!ts) return null
  return Math.round((Date.now() - new Date(ts).getTime()) / 1000 / 60) // minutes
}

// ── Clear (for sign-out) ──────────────────────────────────────────────────────
export function clearOfflineSnapshots() {
  Object.values(KEYS).forEach((k) => {
    try { localStorage.removeItem(k) } catch { /* ignore */ }
  })
}
